import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from "@angular/core"
import { GoogleMap, GoogleMapsModule } from "@angular/google-maps"
import { RegionProperties, StoryProperties } from "./map.type"
import { LngLat, Story } from "redlineLibs"
import { MapService } from "./map.service"
import { Region, regionsData } from "redlineLibs"
import { Feature, FeatureCollection, Geometry } from "geojson"
import { UtilAnalyticsService } from "util/analytics"
import { StorageService } from "../../services/storage.service"

import { GoogleMapsLoaderService } from "../../services/google-maps-loader.service"
import { GeoJsonLoaderService } from "../../services/geoJsonLoaderService"
import { environment } from "../../../environments/environment"
import LatLngLiteral = google.maps.LatLngLiteral
import AdvancedMarkerElementOptions = google.maps.marker.AdvancedMarkerElementOptions
import { RouteService, Section } from "../../services/route.service"

// import LatLng = google.maps.LatLng

interface MapMarker {
  id: string
  position: LatLngLiteral
  options: AdvancedMarkerElementOptions
  properties: StoryProperties
}

/*
interface GeoJsonRegion {
  sourceId: string
  sourceDataUrl: string
  layerId: string
  coordinates: google.maps.LatLngLiteral[]
  properties?: RegionProperties
  geoJsonData?: never
}
*/

@Component({
  imports: [
    GoogleMapsModule
],
  standalone: true,
  template: `
    @if (isGoogleMapsLoaded()) {
      <google-map
        #googleMap
        height="100%"
        width="100%"
        [center]="mapCenter()"
        [zoom]="zoomLevel()"
        [options]="mapOptions()"
        (mapClick)="onMapClick($event)"
        (zoomChanged)="onZoomChanged()"
      >
        <!-- Region polygons -->
        @for (region of loadedRegions(); track region.coordinates) {
          <map-polygon
            [paths]="region.coordinates"
            [options]="getPolygonOptions(region.properties)"
            (polygonClick)="onRegionClick($event, region.properties)"
          />
        }

        <!-- Custom location marker -->
        @if (showMarkers() && markerLocation()) {
          <map-advanced-marker
            [position]="locationMarkerPosition()"
            [options]="locationMarkerOptions()"
          />
        }

        <!-- Story markers -->
        @if (showMarkers() && story()) {
          @for (marker of storyMarkers(); track marker.id) {
            <map-advanced-marker
              [position]="marker.position"
              [options]="marker.options"
              (mapClick)="onContentMarkerClick(marker.properties)"
            />
          }
        }

        <!-- All story markers when no specific story -->
        @if (showMarkers() && !story()) {
          @for (marker of allStoryMarkers(); track marker.id) {
            <map-advanced-marker
              [position]="marker.position"
              [options]="marker.options"
              (mapClick)="onContentMarkerClick(marker.properties)"
            />
          }
        }

        <!-- Pin marker for map clicks -->
        @if (pinMarkerVisible()) {
          <map-advanced-marker
            [position]="pinMarkerPosition()"
            [options]="pinMarkerOptions()"
          />
        }
      </google-map>
    }
  `,
  selector: "e2e-map-google",
})
export class MapGoogleComponent implements OnInit, AfterViewInit {
  private mapService = inject(MapService)
  private storageService = inject(StorageService)
  private analyticsService = inject(UtilAnalyticsService)
  private googleMapsLoader = inject(GoogleMapsLoaderService)
  private geoJsonLoader = inject(GeoJsonLoaderService)
  private routeService = inject(RouteService)

  mapRef = viewChild<GoogleMap>("googleMap")

  // Inputs
  showMarkers = input(false)
  markerLocation = input<LngLat | undefined>(undefined)
  story = input<Story | undefined>(undefined)
  storyZoomLevel = input<number | undefined>(undefined)
  scrollZoom = input(false)
  regionDatum = input.required<Region>()

  // Outputs
  zoomLevelChange = output<number>()
  mapClick = output<LngLat>()
  contentMarkerClick = output<StoryProperties>()
  regionClick = output<{ lngLat: LngLat; properties: RegionProperties | undefined }>()

  isGoogleMapsLoaded = signal(false)

  // Marker icon signals
  markerImage = {
    active: "/assets/images/map-marker-active.png",
    contributed: "/assets/images/map-marker-contributed.png",
    featured: "/assets/images/map-marker-featured.png",
    regional: "/assets/images/map-marker-regional.png",
  }

  // Map state signals
  mapInstance = signal<google.maps.Map | undefined>(undefined)
  pinMarkerPoint = signal<Geometry | null>(null)
  isMapLoaded = signal(false)

  // Region loading signals
  regionUrls = signal<string[]>([])
  // loadedGeoJsonData = signal<Map<string, any>>(new Map())
  // regionLoadingComplete = signal(false)

  // Computed values
  zoomLevel = computed(() =>
    this.storyZoomLevel() || this.regionDatum().zoom || 4
  )

  mapCenter = computed(() => {
    const center =
      this.story()?.settings.location || this.mapService.newCenter() || this.regionDatum().location
    return { lat: center.lat, lng: center.lng }
  })

  mapOptions = computed<google.maps.MapOptions>(() => {
    if (!this.isGoogleMapsLoaded()) {
      return {}
    }

/*
    let mapTypeId: google.maps.MapTypeId
    switch (this.routeService.section()) {
      case Section.REDLINE_MAP:
        mapTypeId = google.maps.MapTypeId.ROADMAP
        break
      default:
        mapTypeId = google.maps.MapTypeId.HYBRID
    }
*/

    return {
      mapId: environment.mapOptionsMapId,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      scrollwheel: this.scrollZoom(),
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      tilt: 40,
    }
  })

  // Initial region configuration
  initialRegions = computed(() =>
    Object.values(regionsData)
      .filter(regionsData => regionsData.enabled)
      .map((regionsData) => ({
        sourceId: regionsData.region + "-sourceId",
        sourceDataUrl: this.storageService.getFileUrl("/assets/geojson/" + regionsData.geoJson),
        layerId: regionsData.region + "-layerId",
        coordinates: [] as google.maps.LatLngLiteral[][],
        regionInfo: regionsData,
      })),
  )

  // Loaded regions with coordinates
  loadedRegions = computed(() => {
    const geoJsonDataMap = this.geoJsonLoader.loadedData()
    const regions = this.initialRegions()

    return regions.flatMap(region => {
      const geoJsonData = geoJsonDataMap.get(region.sourceDataUrl)
      if (!geoJsonData) return []

      // Get all polygon paths from the GeoJSON
      const polygonData = this.extractAllPolygonData(geoJsonData)

      // Create a region for each polygon path
      return polygonData
        .map(data => ({
          // ...region, // Keep all original properties
          coordinates: data.path, // Single LatLng[] path
          properties: data.properties, // Use the individual feature's properties
        }))
    })
  })

  markerWidth = "32px" // "84px" // "36px"
  markerHeight = "64px" // "72px"
  imageWidth = "64px"
  imageHeight = "64px"

  locationMarkerPosition = computed(() => {
    const location = this.markerLocation()
    return location ? { lat: location.lat, lng: location.lng } : { lat: 0, lng: 0 }
  })

  locationMarkerOptions = computed<AdvancedMarkerElementOptions>(() => {
    if (!this.isGoogleMapsLoaded()) {
      return {}
    }

    const img = document.createElement("img")
    img.src = this.storageService.getFileUrl(this.markerImage.active)
    img.style.width = this.markerWidth
    img.style.height = this.markerHeight

    return {
      content: img,
      title: "location marker title"
    }
  })

  pinMarkerVisible = computed(() => !!this.pinMarkerPoint())

  pinMarkerPosition = computed(() => {
    if (!this.isGoogleMapsLoaded()) {
      return { lat: 0, lng: 0 }
    }

    const geometry = this.pinMarkerPoint()
    if (geometry && geometry.type === "Point") {
      return {
        lat: geometry.coordinates[1],
        lng: geometry.coordinates[0],
      }
    }
    return { lat: 0, lng: 0 }
  })

  pinMarkerOptions = computed<AdvancedMarkerElementOptions>(() => {
    if (!this.isGoogleMapsLoaded()) {
      return {}
    }

    const img = document.createElement("img")
    img.src = this.storageService.getFileUrl(this.markerImage.active)
    img.style.width = this.markerWidth
    img.style.height = this.markerHeight

    return {
      content: img,
      title: "pin marker title"
    }
  })

  storyMarkerCollection = computed<FeatureCollection>(() => {
    const story = this.story()
    const storyMarkerFeature = this.mapService.getFeatureFromStory(story)
    const storyMarkerFeatures = storyMarkerFeature && [storyMarkerFeature] || []
    return {
      type: "FeatureCollection",
      features: storyMarkerFeatures,
    }
  })

  storyMarkers = computed<MapMarker[]>(() => {
    const allMarkers: MapMarker[] = []
    this.storyMarkerCollection().features
      .filter((feature) => feature.geometry.type === "Point" && "coordinates" in feature.geometry)
      .forEach((feature, index) => {
        const geometry = feature.geometry
        if (geometry && geometry.type === "Point") {
          const img = document.createElement("img")
          img.src = this.storageService.getFileUrl(this.markerImage.active)
          img.style.width = this.markerWidth
          img.style.height = this.markerHeight

          allMarkers.push({
            id: "story-" + index,
            position: {
              lat: geometry.coordinates[1],
              lng: geometry.coordinates[0],
            },
            options: {
              content: img,
              title: "story marker title"
            },
            properties: feature.properties as StoryProperties,
          })
        }
      })

    return allMarkers
  })

  allStoryMarkers = computed<MapMarker[]>(() => {
    const allMarkers: MapMarker[] = []

    const collections = [
      {
        data: this.mapService.myStoriesMarkerCollection(),
        imageSrc: this.markerImage.contributed,
        prefix: "my",
      },
      {
        data: this.mapService.contributedStoriesMarkerCollection(),
        imageSrc: this.markerImage.contributed,
        prefix: "contributed",
      },
      {
        data: this.mapService.featuredStoriesMarkerCollection(),
        imageSrc: this.markerImage.featured,
        prefix: "featured",
      },
      {
        data: this.mapService.regionalStoriesMarkerCollection(),
        imageSrc: this.markerImage.regional,
        prefix: "regional",
      },
    ]

    collections.forEach(({ data, imageSrc, prefix }) => {
      if (data?.features) {
        data.features
          .forEach((feature: Feature, index: number) => {
            const geometry = feature.geometry
            const properties = feature.properties as StoryProperties
            let imageWidth = this.markerWidth
            let imageHeight = this.markerHeight
            let imageBorderRadius = "0"
            let imageBorder = "0"
            let filePath = imageSrc
            if (properties.filePath) {
              imageWidth = this.imageWidth
              imageHeight = this.imageHeight
              imageBorderRadius = "100%"
              imageBorder = "2px solid #fff"
              filePath = properties.filePath
            }
            if (geometry && geometry.type === "Point") {
              const img = document.createElement("img")
              img.src = this.storageService.getFileUrl(filePath)
              img.style.width = imageWidth
              img.style.height = imageHeight
              img.style.borderRadius = imageBorderRadius
              img.style.border = imageBorder
              img.style.objectFit = "cover"

              allMarkers.push({
                id: prefix + "-" + index,
                position: {
                  lat: geometry.coordinates[1],
                  lng: geometry.coordinates[0],
                },
                options: {
                  content: img,
                  title: "all story markers title"
                },
                properties,
              })
            }
          })
      }
    })

    return allMarkers
  })

  polygonColors = computed(() => ({
    A: { fill: "rgba(121, 255, 97, 0.3)", stroke: "#79FF61" },
    B: { fill: "rgba(97, 194, 255, 0.3)", stroke: "#61C2FF" },
    C: { fill: "rgba(255, 239, 97, 0.3)", stroke: "#FFEF61" },
    default: { fill: "rgba(255, 97, 97, 0.3)", stroke: "#FF6161" },
  }))

  constructor() {
    this.analyticsService.sendScreenViewEvent(this.constructor.name, this.constructor.name)

    // Effect to start loading region data
    effect(() => {
      if (this.isGoogleMapsLoaded()) {
        const regions = this.initialRegions()
        const urls = regions.map(r => r.sourceDataUrl)
        this.regionUrls.set(urls)
      }
    })

    // Effect to load GeoJSON data when URLs change
    effect(() => {
      const urls = this.regionUrls()
      if (this.isGoogleMapsLoaded() && urls.length > 0) {
        this.loadRegionsData(urls)
      }
    })
  }

  async ngOnInit() {
    try {
      await this.googleMapsLoader.load()
      this.isGoogleMapsLoaded.set(true)
    } catch (error) {
      console.error("Failed to load Google Maps:", error)
    }
  }

  ngAfterViewInit() {
    // Only try to access googleMapRef if Google Maps is loaded and the element exists
    const googleMapElement = this.mapRef()?.googleMap
    if (this.isGoogleMapsLoaded() && googleMapElement) {
      this.mapInstance.set(googleMapElement)
      this.isMapLoaded.set(true)
    }

  }

  private async loadRegionsData(urls: string[]) {
    for (const url of urls) {
      try {
        await this.geoJsonLoader.loadGeoJson(url)
      } catch (error) {
        console.error(`Failed to load region data from ${url}:`, error)
      }
    }
  }

  private extractAllPolygonData(geoJsonData: FeatureCollection): {
    path: google.maps.LatLng[],
    properties: RegionProperties
  }[] {
    if (!geoJsonData?.features) {
      return []
    }
    const allData: Array<{ path: google.maps.LatLng[], properties: RegionProperties }> = []
    geoJsonData.features.forEach(feature => {
      if (feature.geometry) {
        const geometry = feature.geometry
        const properties = feature.properties || {} // Extract properties from each feature
        if (geometry.type === "Polygon") {
          const outerRing = geometry.coordinates[0]
          const path = outerRing.map((coordinate: number[]) =>
            new google.maps.LatLng(coordinate[1], coordinate[0]),
          )
          allData.push({ path, properties })
        } else if (geometry.type === "MultiPolygon") {
          geometry.coordinates.forEach((polygonCoords: number[][][]) => {
            const outerRing = polygonCoords[0]
            const path = outerRing.map((coordinate: number[]) =>
              new google.maps.LatLng(coordinate[1], coordinate[0]),
            )
            allData.push({ path, properties }) // Each polygon gets the same feature properties
          })
        }
      }
    })

    return allData
  }

  getPolygonOptions(regionProperties: RegionProperties): google.maps.PolygonOptions {
    if (!this.isGoogleMapsLoaded()) {
      return {}
    }

    const colors = this.polygonColors()
    const grade = regionProperties?.grade || "default"
    const color = colors[grade as keyof typeof colors] || colors.default

    return {
      fillColor: color.stroke, // Use the same color as stroke
      fillOpacity: 0.3,
      strokeColor: color.stroke,
      strokeOpacity: 0.3, // Make stroke same opacity as fill
      strokeWeight: 2,
      clickable: true,
    }

  }

  onRegionClick(event: google.maps.MapMouseEvent, regionProperties: RegionProperties) {
    // console.log("onRegionClick", event, regionProperties)
    // if (!event.latLng) return

    const lngLat = {
      lat: event.latLng?.lat() || 0,
      lng: event.latLng?.lng() || 0,
    }

    this.regionClick.emit({ lngLat, properties: regionProperties })
    this.mapService.setMarkerLocation(lngLat)
    this.mapService.setShowMapModal(true)
    if (this.showMarkers()) {
      this.mapClick.emit(lngLat)
    }
  }


  onMapClick(event: google.maps.MapMouseEvent) {
    // console.log("onMapClick", event)
    const lngLat = {
      lat: event.latLng?.lat() || 0,
      lng: event.latLng?.lng() || 0,
    }

    this.mapClick.emit(lngLat)

    // Set pin marker
    this.pinMarkerPoint.set({
      type: "Point",
      coordinates: [lngLat.lng, lngLat.lat],
    })
  }

  onContentMarkerClick(properties: StoryProperties) {
    // console.log("onContentMarkerClick", properties)
    this.contentMarkerClick.emit(properties)
  }

  onZoomChanged() {
    const zoom = this.mapRef()?.googleMap?.getZoom()
    if (zoom !== undefined) {
      this.zoomLevelChange.emit(zoom)
    }
  }

}
